// src/features/system/developer/components/video-gallery/hooks/useMediaSession.ts
// ────────────────────────────────────────────────────────────────────────────
// Media Session API integration — OS-level media controls parity.
//
// When the active slide is playing, this hook publishes metadata (title,
// artist, artwork) to the browser's Media Session, and wires the standard
// action handlers (play / pause / next / previous / seek). The result:
//   • macOS Control Center / Now Playing widget shows the video.
//   • Android lock-screen / notification controls work.
//   • Bluetooth headphones play/pause buttons control the feed.
//   • Hardware media keys (F7/F8/F9) work.
//
// Single-flight per active slide. Cleared when the slide is no longer
// active or the component unmounts. Graceful no-op on browsers without
// the API (older Safari, Firefox).
// ────────────────────────────────────────────────────────────────────────────
import { useEffect } from 'react';

interface MediaSessionLike {
  metadata: unknown;
  playbackState: 'none' | 'playing' | 'paused';
  setActionHandler: (
    action: string,
    handler: ((details?: unknown) => void) | null,
  ) => void;
  setPositionState?: (state: {
    duration?: number;
    playbackRate?: number;
    position?: number;
  }) => void;
}

function getMediaSession(): MediaSessionLike | null {
  if (typeof navigator === 'undefined') return null;
  const nav = navigator as unknown as { mediaSession?: MediaSessionLike };
  return nav.mediaSession ?? null;
}

interface Args {
  isActive: boolean;
  videoEl: HTMLVideoElement | null;
  title: string;
  artist?: string;
  artworkUrl?: string;
  onPlay: () => void;
  onPause: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

const ALL_ACTIONS = [
  'play',
  'pause',
  'previoustrack',
  'nexttrack',
  'seekto',
  'seekbackward',
  'seekforward',
] as const;

/**
 * Publish OS-level media-session metadata + action handlers while a slide
 * is active. Idempotent: clears on deactivation/unmount.
 */
export function useMediaSession({
  isActive, videoEl, title, artist, artworkUrl, onPlay, onPause, onNext, onPrevious,
}: Args): void {
  // Metadata + action handlers.
  useEffect(() => {
    const ms = getMediaSession();
    if (!ms || !isActive) return;

    // MediaMetadata constructor isn't typed on older lib.dom; guard it.
    const MM = (globalThis as unknown as {
      MediaMetadata?: new (init: {
        title: string;
        artist?: string;
        album?: string;
        artwork?: { src: string; sizes?: string; type?: string }[];
      }) => unknown;
    }).MediaMetadata;

    if (MM) {
      try {
        ms.metadata = new MM({
          title,
          artist: artist || 'PACMaster',
          album: 'Video Feed',
          artwork: artworkUrl
            ? [{ src: artworkUrl, sizes: '512x512', type: 'image/png' }]
            : [],
        });
      } catch { /* ignore */ }
    }

    const safeSet = (action: typeof ALL_ACTIONS[number], handler: (() => void) | null) => {
      try { ms.setActionHandler(action, handler as never); } catch { /* unsupported action */ }
    };

    safeSet('play', onPlay);
    safeSet('pause', onPause);
    safeSet('nexttrack', onNext ?? null);
    safeSet('previoustrack', onPrevious ?? null);
    safeSet('seekbackward', () => {
      if (videoEl) videoEl.currentTime = Math.max(0, videoEl.currentTime - 5);
    });
    safeSet('seekforward', () => {
      if (videoEl && videoEl.duration) {
        videoEl.currentTime = Math.min(videoEl.duration, videoEl.currentTime + 5);
      }
    });
    safeSet('seekto', ((details?: { seekTime?: number }) => {
      if (videoEl && details && typeof details.seekTime === 'number') {
        videoEl.currentTime = details.seekTime;
      }
    }) as unknown as () => void);

    return () => {
      // Only tear down what we set. We do NOT clear metadata wholesale
      // because the next active slide will immediately overwrite it; a
      // hard nullification would cause a flicker in OS chrome.
      for (const a of ALL_ACTIONS) safeSet(a, null);
    };
  }, [isActive, title, artist, artworkUrl, onPlay, onPause, onNext, onPrevious, videoEl]);

  // Keep playback state mirrored so OS controls show the correct icon.
  useEffect(() => {
    const ms = getMediaSession();
    if (!ms || !isActive || !videoEl) return;

    const sync = () => {
      ms.playbackState = videoEl.paused ? 'paused' : 'playing';
      try {
        ms.setPositionState?.({
          duration: isFinite(videoEl.duration) ? videoEl.duration : 0,
          playbackRate: videoEl.playbackRate,
          position: videoEl.currentTime,
        });
      } catch { /* invalid position state values — ignore */ }
    };

    sync();
    videoEl.addEventListener('play', sync);
    videoEl.addEventListener('pause', sync);
    videoEl.addEventListener('timeupdate', sync);
    videoEl.addEventListener('loadedmetadata', sync);
    return () => {
      videoEl.removeEventListener('play', sync);
      videoEl.removeEventListener('pause', sync);
      videoEl.removeEventListener('timeupdate', sync);
      videoEl.removeEventListener('loadedmetadata', sync);
    };
  }, [isActive, videoEl]);
}
